import { Fragment, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, ExternalLink, MoreHorizontal } from "lucide-react";
import { formatMoney } from "../api";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { fmtCtr, fmtInt, fmtMoneyWhole, fmtNum1 } from "../ads";
import { reportRangeQuery } from "../date-range";
import {
  budgetRange, objectiveWords, pctChange, statusWords, switchable, tone,
  type FbAdsReport, type FbCampaign, type FbObject, type Totals,
} from "../facebook";
import { AdsRange } from "../components/AdsRange";
import { AreaChart, ChartCard, ChartEmpty } from "../components/Charts";
import Dialog from "../components/Dialog";
import QuickMenu, { QuickMenuItem } from "../components/QuickMenu";
import { EmptyState, ErrorState, MetricCard, PageHeader, PageSkeleton } from "../components/Ui";
import { NoClient, useMutation } from "../components/Workflow";
import { AccountPicker, ChangesList, Freshness, SyncButton, WritesNotice } from "../components/facebook/FbShared";

const leadsText = (n: number | null) => (n === null ? "No lead figure" : `${fmtInt(n)} ${n === 1 ? "lead" : "leads"}`);
const shortDate = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

type Target = { level: "campaign" | "adset" | "ad"; id: string; name: string; status: string | null; configured: string | null; daily_budget: number | null };
type Pending = { kind: "status"; target: Target; on: boolean } | { kind: "budget"; target: Target };
type Metric = "spend" | "leads" | "clicks";
type TargetSource = { id?: string; object_id?: string; name: string | null; status: string | null; configured_status: string | null; daily_budget: number | null };

/** A client's Facebook and Instagram ads: what the spend bought, and its campaigns, ad sets and ads with their switches. */
export default function FacebookAds() {
  const { clientId } = useClientSelection();
  if (!clientId) return <><PageHeader title="Facebook ads" description="Pick a client in the sidebar." /><NoClient /></>;
  return <AdsView key={clientId} clientId={clientId} />;
}

function AdsView({ clientId }: { clientId: number }) {
  const { user } = useAuth();
  const admin = user?.role === "superadmin";
  const canAsk = user?.role !== "client";
  const [params] = useSearchParams();
  const link = params.get("link") || "";
  const { data, loading, error, reload } = useApi<FbAdsReport>(`/api/facebook/${clientId}/ads?${reportRangeQuery(params)}${link ? `&link=${link}` : ""}`);
  const m = useMutation();
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [metric, setMetric] = useState<Metric>("spend");
  const [runBusy, setRunBusy] = useState(false);
  const say = (text: string, ok: boolean) => { setNotice({ ok, text }); reload(); };

  if (loading && !data) return <><PageHeader title="Facebook ads" description="Loading…" /><PageSkeleton cards={3} /></>;
  if (error || !data) return <><PageHeader title="Facebook ads" /><ErrorState message={error || "Facebook ads could not load."} onRetry={reload} /></>;
  if (!data.link || !data.period || !data.totals) {
    return (
      <>
        <PageHeader title="Facebook ads" />
        <EmptyState title="No Facebook ad account linked"
          description={admin ? "Link this client's Meta ad account through Windsor in Setup, Connections. Spend, leads and campaigns show here after the first read." : "A superadmin links the ad account in Setup, Connections."}
          action={admin ? <Link className="button secondary" to={`/client-settings?client_id=${clientId}`}>Open Connections</Link> : undefined} />
      </>
    );
  }
  const { totals, previous, period, campaigns = [], adsets = [], ads = [], changes = [] } = data;
  const acc = data.link;
  const writes = acc.writes_enabled && canAsk;
  const confirmLabel = admin ? "Apply now" : "Send for approval";
  const toggle = (id: string) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const ask = async (action: string, params: Record<string, unknown>, title: string) => {
    setNotice(null);
    const r = await m.run<{ message: string }>(`/api/facebook/${clientId}/actions`, { link_id: acc.id, action, params, title, apply_now: admin });
    if (!r) return false;
    say(r.message, true);
    return true;
  };
  const change = (k: keyof Totals) => (previous ? pctChange(totals[k] as number | null, previous[k] as number | null) : null);
  const extras = [totals.messages ? `${fmtInt(totals.messages)} messages` : null, totals.calls ? `${fmtInt(totals.calls)} calls` : null].filter(Boolean).join(", ");
  const days = data.daily || [];
  const hasDays = days.some((d) => d.spend || d.clicks || d.leads);

  const target = (level: Target["level"], o: TargetSource): Target =>
    ({ level, id: (o.id || o.object_id)!, name: o.name || "Untitled", status: o.status, configured: o.configured_status, daily_budget: o.daily_budget });

  const statusCell = (t: Target) => {
    const s = statusWords(t.status || t.configured);
    const chip = <span className={`campaign-status ${s.on ? "on" : ""}`}>{s.label}</span>;
    if (!writes || !switchable(t.configured)) return chip;
    return (
      <QuickMenu label={`Status of ${t.name}`} trigger={chip}>
        {(close) => (<>
          <QuickMenuItem active={t.configured === "ACTIVE"} onClick={() => { close(); if (t.configured !== "ACTIVE") setPending({ kind: "status", target: t, on: true }); }}>On</QuickMenuItem>
          <QuickMenuItem active={t.configured === "PAUSED"} onClick={() => { close(); if (t.configured !== "PAUSED") setPending({ kind: "status", target: t, on: false }); }}>Paused</QuickMenuItem>
        </>)}
      </QuickMenu>
    );
  };
  const budgetCell = (t: Target, lifetime: number | null) => {
    if (t.daily_budget === null) return lifetime !== null ? <span title="Lifetime budget">{formatMoney(lifetime)} total</span> : <span className="muted">—</span>;
    return writes && t.level !== "ad"
      ? <button type="button" className="text-button campaigns-budget" onClick={() => setPending({ kind: "budget", target: t })}>{formatMoney(t.daily_budget)}</button>
      : <>{formatMoney(t.daily_budget)}</>;
  };
  const rowMenu = (t: Target) => (!writes || !switchable(t.configured) ? null : (
    <QuickMenu align="right" label={`Actions for ${t.name}`} trigger={<MoreHorizontal aria-hidden="true" />}>
      {(close) => (<>
        <QuickMenuItem onClick={() => { close(); setPending({ kind: "status", target: t, on: t.configured !== "ACTIVE" }); }}>{t.configured === "ACTIVE" ? `Pause ${t.level === "adset" ? "ad set" : t.level}` : `Turn on ${t.level === "adset" ? "ad set" : t.level}`}</QuickMenuItem>
        {t.level !== "ad" && t.daily_budget !== null && <QuickMenuItem onClick={() => { close(); setPending({ kind: "budget", target: t }); }}>Change daily budget</QuickMenuItem>}
      </>)}
    </QuickMenu>
  ));

  return (
    <div className="fb-page">
      <PageHeader
        title="Facebook ads"
        description={`${acc.account_name}. ${shortDate(period.from)} to ${shortDate(period.to)}.`}
        actions={<>
          <AccountPicker links={data.links} current={acc} label="Facebook ad account" />
          <AdsRange />
          <SyncButton clientId={clientId} kind="ads" onDone={say} />
        </>}
      />
      <Freshness link={acc} />
      {notice && <p className={`inline-notice ${notice.ok ? "success" : "danger"}`} role={notice.ok ? "status" : "alert"}><span>{notice.text}</span><button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}>×</button></p>}
      {canAsk && <WritesNotice link={acc} admin={admin} clientId={clientId} onChanged={reload} what="pause, turn on and change budgets" />}
      {!pending && m.feedback}

      <section className="metric-grid ads-metrics fb-metrics" aria-label="Totals">
        <MetricCard label="Spend" value={fmtMoneyWhole(totals.spend)} change={change("spend")} tone={tone("spend", change("spend"))} />
        <MetricCard label="Leads" value={fmtInt(totals.leads)} change={change("leads")} tone={tone("leads", change("leads"))} detail={extras ? `Plus ${extras}` : undefined} />
        <MetricCard label="Cost per lead" value={formatMoney(totals.cpl)} change={change("cpl")} tone={tone("cpl", change("cpl"))} />
        <MetricCard label="Clicks" value={fmtInt(totals.clicks)} change={change("clicks")} tone={tone("clicks", change("clicks"))} detail={totals.link_clicks !== null ? `${fmtInt(totals.link_clicks)} to the site or form` : undefined} />
        <MetricCard label="Click rate" value={fmtCtr(totals.ctr)} change={change("ctr")} tone={tone("ctr", change("ctr"))} />
        <MetricCard label="Cost per 1,000 views" value={formatMoney(totals.cpm)} change={change("cpm")} tone={tone("cpm", change("cpm"))} />
      </section>
      <p className="ads-note">{previous ? `Compared with ${shortDate(period.previous.from)} to ${shortDate(period.previous.to)}.` : "No Facebook figures for the period before, so there is no comparison."} Leads are what Meta counts as a lead: forms on Facebook and leads your pixel reports.</p>

      <div className="dashboard-charts">
        <ChartCard className="chart-wide" eyebrow="Facebook" title={`${metric === "spend" ? "Spend" : metric === "leads" ? "Leads" : "Clicks"} per day`}
          meta={<div className="segmented-control compact" aria-label="Chart metric">
            {(["spend", "leads", "clicks"] as const).map((k) => <button key={k} type="button" className={metric === k ? "active" : ""} aria-pressed={metric === k} onClick={() => setMetric(k)}>{k === "spend" ? "Spend" : k === "leads" ? "Leads" : "Clicks"}</button>)}
          </div>}>
          {hasDays
            ? <AreaChart integer={metric !== "spend"} data={days.map((d) => ({ label: shortDate(d.date), value: d[metric] ?? 0 }))} format={metric === "spend" ? (v) => formatMoney(v, true) : fmtInt} caption={`${metric} per day`} />
            : <ChartEmpty>No Facebook ad activity in this range.</ChartEmpty>}
        </ChartCard>
      </div>

      <section className="panel campaigns-panel fb-campaigns">
        <div className="panel-heading"><div><h2>Campaigns</h2><p className="panel-note">Open a campaign for its ad sets and ads (last 30 days).</p></div></div>
        {campaigns.length ? (
          <div className="table-wrap">
            <table className="ads-table campaigns-table fb-table">
              <thead><tr>
                <th>Campaign</th><th>Status</th><th className="num">Daily budget</th><th className="num">Spend</th><th className="num">Leads</th>
                <th className="num">Cost per lead</th><th className="num fb-col-extra">Clicks</th><th className="num fb-col-extra">Click rate</th><th><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>{campaigns.map((c) => {
                const t = target("campaign", c);
                const isOpen = open.has(c.id);
                const children = adsets.filter((a) => a.campaign_id === c.id);
                const loose = ads.filter((a) => a.campaign_id === c.id && !children.some((s) => s.object_id === a.adset_id));
                return (
                  <Fragment key={c.id}>
                    <tr className={isOpen ? "fb-open" : undefined}>
                      <td className="campaigns-name-cell">
                        <button type="button" className="fb-expand" aria-expanded={isOpen} onClick={() => toggle(c.id)}>
                          {isOpen ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                          <span><strong className="campaigns-name" title={c.name}>{c.name}</strong>{objectiveWords(c.objective) && <small className="campaigns-serving">{objectiveWords(c.objective)}</small>}</span>
                        </button>
                      </td>
                      <td data-label="Status">{statusCell(t)}</td>
                      <td className="num" data-label="Daily budget">{budgetCell(t, c.lifetime_budget)}</td>
                      <td className="num" data-label="Spend">{fmtMoneyWhole(c.spend)}</td>
                      <td className="num" data-label="Leads">{fmtInt(c.leads)}</td>
                      <td className="num" data-label="Cost per lead">{formatMoney(c.cpl)}</td>
                      <td className="num fb-col-extra" data-label="Clicks">{fmtInt(c.clicks)}</td>
                      <td className="num fb-col-extra" data-label="Click rate">{fmtCtr(c.ctr)}</td>
                      <td className="campaigns-actions">{c.known ? rowMenu(t) : null}</td>
                    </tr>
                    {isOpen && (
                      <tr className="fb-children"><td colSpan={9}>
                        <Children campaign={c} adsets={children} ads={ads} loose={loose} statusCell={statusCell} budgetCell={budgetCell} rowMenu={rowMenu} target={target} />
                      </td></tr>
                    )}
                  </Fragment>
                );
              })}</tbody>
            </table>
          </div>
        ) : <EmptyState title="No campaigns in this range" description="Nothing ran in these dates. Pick a longer range, or press Sync now if the account was linked today." />}
      </section>

      <ChangesList changes={changes} admin={admin} clientId={clientId} onRan={say} busy={runBusy} setBusy={setRunBusy} />

      {pending?.kind === "status" && (
        <Dialog title={pending.on ? `Turn on "${pending.target.name}"?` : `Pause "${pending.target.name}"?`} onClose={() => setPending(null)}>
          <div className="operation-form">
            <p className="campaigns-dialog-copy">{pending.on
              ? `It starts delivering again${pending.target.daily_budget !== null ? ` and can spend up to ${formatMoney(pending.target.daily_budget)} a day` : ""}.`
              : `Its ads stop showing until it is turned on again.${pending.target.level === "campaign" ? " Its ad sets and ads keep their own settings." : ""}`}</p>
            {!admin && <p className="campaigns-hint">A superadmin approves it in Approvals, then runs it from this page.</p>}
            {m.error && <p className="inline-notice danger" role="alert">{m.error}</p>}
            <div className="operation-actions">
              <button type="button" className="button secondary" onClick={() => setPending(null)}>Cancel</button>
              <button type="button" className="button" disabled={m.busy} onClick={async () => {
                const t = pending.target;
                if (await ask(`${pending.on ? "enable" : "pause"}_${t.level}`, { [`${t.level}_id`]: t.id }, t.name)) setPending(null);
              }}>{m.busy ? "Working…" : !admin ? "Send for approval" : pending.on ? "Turn on" : "Pause"}</button>
            </div>
          </div>
        </Dialog>
      )}
      {pending?.kind === "budget" && pending.target.daily_budget !== null && (
        <BudgetDialog target={pending.target} label={confirmLabel} busy={m.busy} error={m.error} onCancel={() => setPending(null)}
          onSave={async (daily) => { const t = pending.target; if (await ask(`set_${t.level}_budget`, { [`${t.level}_id`]: t.id, budget_type: "daily", amount: Math.round(daily * 100) }, t.name)) setPending(null); }} />
      )}
    </div>
  );
}

function Children({ campaign, adsets, ads, loose, statusCell, budgetCell, rowMenu, target }: {
  campaign: FbCampaign; adsets: FbObject[]; ads: FbObject[]; loose: FbObject[];
  statusCell: (t: Target) => ReactNode; budgetCell: (t: Target, lifetime: number | null) => ReactNode; rowMenu: (t: Target) => ReactNode;
  target: (level: Target["level"], o: TargetSource) => Target;
}) {
  if (!adsets.length && !loose.length) return <p className="fb-none">{campaign.known ? "No ad sets with activity in the last 30 days." : "Meta did not describe this campaign in the last read. Press Sync now."}</p>;
  const adRow = (a: FbObject) => {
    const t = target("ad", a);
    return (
      <li key={a.object_id} className="fb-ad">
        {a.thumbnail_url ? <img src={a.thumbnail_url} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} /> : <span className="fb-ad-blank" aria-hidden="true" />}
        <div className="fb-ad-main">
          <strong title={a.name || ""}>{a.name || "Untitled ad"}</strong>
          {(a.title || a.body) && <span className="muted">{a.title || a.body}</span>}
        </div>
        <span className="fb-ad-status">{statusCell(t)}</span>
        <span className="fb-ad-figs"><span>{fmtMoneyWhole(a.spend)}</span><span>{leadsText(a.leads)}</span></span>
        <span className="fb-ad-go">
          {a.permalink && <a className="icon-button" href={a.permalink} target="_blank" rel="noreferrer noopener" aria-label={`See ${a.name || "the ad"} on Facebook`}><ExternalLink aria-hidden="true" /></a>}
          {rowMenu(t)}
        </span>
      </li>
    );
  };
  return (
    <div className="fb-tree">
      {adsets.map((s) => {
        const t = target("adset", s);
        const mine = ads.filter((a) => a.adset_id === s.object_id);
        return (
          <section key={s.object_id} className="fb-adset">
            <div className="fb-adset-head">
              <div className="fb-adset-name"><span className="fb-kind">Ad set</span><strong title={s.name || ""}>{s.name || "Untitled ad set"}</strong></div>
              <span>{statusCell(t)}</span>
              <span className="fb-adset-budget">{s.daily_budget === null && s.lifetime_budget === null && campaign.daily_budget !== null ? <span className="muted">Campaign budget</span> : budgetCell(t, s.lifetime_budget)}</span>
              <span className="fb-ad-figs"><span>{fmtMoneyWhole(s.spend)}</span><span>{leadsText(s.leads)}</span>{s.leads ? <span>{formatMoney((s.spend || 0) / s.leads)} each</span> : null}</span>
              <span className="campaigns-actions">{rowMenu(t)}</span>
            </div>
            {mine.length ? <ul className="fb-ads">{mine.map(adRow)}</ul> : <p className="fb-none">No ads with activity in the last 30 days.</p>}
          </section>
        );
      })}
      {loose.length > 0 && <ul className="fb-ads">{loose.map(adRow)}</ul>}
      <p className="fb-none">Ad set and ad figures cover the last 30 days.</p>
    </div>
  );
}

function BudgetDialog({ target, label, busy, error, onCancel, onSave }: { target: Target; label: string; busy: boolean; error: string; onCancel: () => void; onSave: (daily: number) => void }) {
  const now = target.daily_budget || 0;
  const range = budgetRange(now);
  const [value, setValue] = useState(String(now));
  const n = Math.round(Number(value) * 100) / 100;
  const outside = !(n >= range.min && n <= range.max);
  function submit(e: FormEvent) { e.preventDefault(); if (!outside && n !== now) onSave(n); }
  return (
    <Dialog title="Change daily budget" description={target.name} onClose={onCancel}>
      <form className="operation-form" onSubmit={submit}>
        <label>Daily budget
          <span className="campaigns-money"><span aria-hidden="true">$</span><input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} autoFocus /></span>
        </label>
        <p className="campaigns-hint">Now {formatMoney(now)} a day. Budgets move at most 20% at a time, so {formatMoney(range.min)} to {formatMoney(range.max)}. Big jumps restart Meta's learning.</p>
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" className="button" disabled={busy || outside || n === now}>{busy ? "Working…" : label}</button>
        </div>
      </form>
    </Dialog>
  );
}
